import React, { useState, useMemo } from 'react';
import { Card, Button, Badge, Modal, Table } from 'react-bootstrap';
import { ClipboardList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFetchHealthDataQuery } from '#store/apis/healthDataApi';
import { Routes } from '#Routes';
import { flattenHealthInterviewDataEntries } from '#utils/healthInterviewDisplay';
import moment from 'moment';

/**
 * Odpowiednik v0 `health-interview-card.tsx` — tabela w obramowaniu; data + „Zobacz wszystkie” (modal).
 * Przycisk aktualizacji wywiadu tylko w modalu / przy braku danych (utwórz).
 */
export function HealthInterviewCard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLanguage = i18n.language;
  const [showModal, setShowModal] = useState(false);
  const {
    data: healthDataResponse,
    isLoading,
    error,
  } = useFetchHealthDataQuery({
    limit: 1,
    offset: 0,
  });

  const closeModal = () => setShowModal(false);

  const latestInterview = useMemo(() => {
    if (!healthDataResponse?.results || healthDataResponse.results.length === 0) {
      return null;
    }
    return healthDataResponse.results[0];
  }, [healthDataResponse]);

  const translateFieldName = (key) => {
    if (key.endsWith('-Comment')) {
      const baseKey = key.replace('-Comment', '');
      const baseFieldName = translateFieldName(baseKey);
      return `${baseFieldName} - ${t('shared-access.public-view.comment')}`;
    }

    const translationKey = `shared-access.public-view.health-interview-fields.${key}`;
    const translated = t(translationKey);
    return translated !== translationKey ? translated : key;
  };

  const translateFieldValue = (key, value) => {
    if (!value || value === null || value === undefined) {
      return '-';
    }

    if (Array.isArray(value)) {
      return value.map((v) => translateFieldValue(key, v)).join(', ');
    }

    const valueStr = String(value);

    const translationKey = `shared-access.public-view.health-interview-values.${key}.${valueStr}`;
    const translated = t(translationKey);

    if (translated !== translationKey) {
      return translated;
    }

    const genericTranslationKey = `shared-access.public-view.health-interview-values.${valueStr}`;
    const genericTranslated = t(genericTranslationKey);
    if (genericTranslated !== genericTranslationKey) {
      return genericTranslated;
    }

    return valueStr;
  };

  const dateFormat = currentLanguage === 'pl' ? 'DD.MM.YYYY' : 'MMM D, YYYY';
  const dateFormatModal = currentLanguage === 'pl' ? 'DD.MM.YYYY HH:mm' : 'MMM D, YYYY HH:mm';

  const interviewShell = (body) => (
    <Card className='health-interview-bootstrap-card border-0 shadow h-100 d-flex flex-column'>
      <Card.Header className='bg-white border-bottom py-3 pb-2'>
        <div
          className='mb-0 d-flex align-items-center gap-2 fw-semibold text-dark'
          style={{ fontSize: '1rem' }}
        >
          <ClipboardList
            className='flex-shrink-0'
            style={{ width: '1.25rem', height: '1.25rem', color: 'var(--primary)' }}
            strokeWidth={2}
            aria-hidden
          />
          {t('dashboard.latest-health-interview')}
        </div>
      </Card.Header>
      <Card.Body className='pt-3 d-flex flex-column flex-grow-1'>{body}</Card.Body>
    </Card>
  );

  if (isLoading) {
    return interviewShell(
      <div className='text-center py-4 flex-grow-1 d-flex align-items-center justify-content-center'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>,
    );
  }

  if (error || !latestInterview) {
    return interviewShell(
      <div className='text-center py-4 flex-grow-1 d-flex flex-column justify-content-center'>
        <p className='text-muted mb-3'>{t('dashboard.no-health-interview-data')}</p>
        <p className='text-muted small mb-3'>{t('dashboard.create-first-interview-message')}</p>
        <Button
          variant='logo-green'
          size='sm'
          className='align-self-center'
          onClick={() => navigate(Routes.HealthDataSurvey.path)}
        >
          {t('dashboard.create-health-interview')}
        </Button>
      </div>,
    );
  }

  const interviewData = latestInterview.data || latestInterview;
  const allDataEntries =
    interviewData && typeof interviewData === 'object' ? flattenHealthInterviewDataEntries(interviewData) : [];

  const importantFields = [
    'age',
    'gender',
    'height',
    'waist_cm',
    'smoking_status',
    'weight',
    'conditions',
    'symptoms',
  ];
  const displayedEntries = allDataEntries
    .filter(([key]) => importantFields.includes(key))
    .slice(0, 6);

  const previewEntries =
    displayedEntries.length > 0 ? displayedEntries : allDataEntries.slice(0, 6);

  const dataEntries = allDataEntries;

  const openModal = () => {
    if (latestInterview && dataEntries && dataEntries.length > 0) {
      setShowModal(true);
    }
  };

  const interviewDate = moment(latestInterview.created_at || latestInterview.created).format(
    dateFormat,
  );

  return (
    <>
      {interviewShell(
        <>
          <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3'>
            <span className='health-interview-date text-muted small'>{interviewDate}</span>
            {latestInterview && dataEntries.length > 0 && (
              <Button variant='link' className='text-decoration-none p-0 small' onClick={openModal}>
                {t('dashboard.view-all')} &gt;
              </Button>
            )}
          </div>
          {previewEntries.length > 0 ? (
            <div className='health-interview-table-wrap flex-grow-1'>
              <table className='health-interview-inner-table w-100 mb-0'>
                <tbody>
                  {previewEntries.map(([key, value]) => (
                    <tr key={key}>
                      <td className='health-interview-td-label'>{translateFieldName(key)}</td>
                      <td className='health-interview-td-value'>
                        {translateFieldValue(key, value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='text-center py-3'>
              <p className='text-muted mb-2 small'>{t('dashboard.no-health-interview-data')}</p>
              <Button
                variant='logo-green'
                size='sm'
                onClick={() => navigate(Routes.HealthDataSurvey.path)}
              >
                {t('dashboard.create-health-interview')}
              </Button>
            </div>
          )}
        </>,
      )}
      {latestInterview && (
        <Modal size='xl' show={showModal} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('dashboard.latest-health-interview')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='mb-3'>
              <Badge bg='success' className='me-2'>
                {t('shared-access.public-view.latest')}
              </Badge>
              <span className='text-muted'>
                {t('shared-access.public-view.interview-date')}:{' '}
                {moment(latestInterview.created_at || latestInterview.created).format(
                  dateFormatModal,
                )}
              </span>
            </div>
            {dataEntries.length > 0 ? (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>
                      {t('shared-access.public-view.interview-questions')}
                    </th>
                    <th style={{ width: '60%' }}>
                      {t('shared-access.public-view.interview-answers')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dataEntries.map(([key, value]) => (
                    <tr key={key}>
                      <td>
                        <strong>{translateFieldName(key)}</strong>
                      </td>
                      <td>{translateFieldValue(key, value)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className='text-muted text-center mb-0'>
                {t('shared-access.public-view.no-interview-data')}
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={closeModal}>
              {t('dashboard.close')}
            </Button>
            <Button
              variant='logo-green'
              onClick={() => {
                closeModal();
                navigate(Routes.HealthDataSurvey.path);
              }}
            >
              {t('dashboard.update-health-interview')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}
